/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Button, Loader, ModalConfirm, Text } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { showSuccess } from "~/components/Toast";
import { EmployeeRole } from "~/models";
import {
  generateActionConfirmText,
  generateActionSuccessText,
  useAxios,
} from "~/tools";

type DeleteRoleProps = {
  role: EmployeeRole;
  deleteCallback: () => void;
  fetchTeam: () => void;
};

function DeleteRole({ role, deleteCallback, fetchTeam }: DeleteRoleProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [attachments, setAttachments] = React.useState(0);
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const fetchAttachments = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/roles/${role.id}/attachments`);
      setAttachments(data.employeesNumber);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, role.id, showError]);

  React.useEffect(() => {
    isOpen && fetchAttachments();
  }, [fetchAttachments, isOpen]);

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        type="button"
        css={{ marginLeft: "0.5rem" }}
        size="sm"
        severity="medium"
        icon="trash"
      />
      <ModalConfirm
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        confirm={async () => {
          try {
            await Axios().delete(`/roles/${role?.id}`);
            setIsOpen(true);
            deleteCallback();
            fetchTeam();
            showSuccess(
              generateActionSuccessText(`The ${role?.name}`)()("deleted")()
            );
          } catch (e) {
            showError(e.response);
          }
          setIsOpen(false);
        }}
        actionLabel="Delete"
        contentLabel="Delete Role"
      >
        {generateActionConfirmText(`${role?.name}`)("delete")()} <br />
        {isLoading ? (
          <Loader inContent />
        ) : (
          attachments !== 0 && (
            <Text color="danger">
              {role?.name} role is assigned to{" "}
              <Text color="danger" tag="span" weight="bold">
                {attachments}
              </Text>{" "}
              employee{attachments > 1 && "s"}.
            </Text>
          )
        )}
      </ModalConfirm>
    </>
  );
}

export { DeleteRole };
